import { CSSProperties, useState } from 'react';
import CardSymphony from 'elements/CardSymphony';
import { clsx, makeKey, styx } from 'modules/Utils';
import WebsiteBuilderHeader from '../WebsiteBuilderHeader';
import InputSymphony from 'elements/InputSymphony';
import useWebsiteBuilder, { WebsiteBuilder_tabsExists } from 'Hooks/useWebsiteBuilder';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import DebugBox from 'components/shareable/DebugBox';
import WebsiteBuilderFooter from '../WebsiteBuilderFooter';
import ColorPickerInput from 'elements/ColorPickerInput';
import MultiPalette from 'elements/MultiPalette';
import SwitchSymphony from 'elements/SwitchSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';

interface TabsContentProps {
    className: string;
    contentIndex: number;
    id: string;
}

const TabsContent = ({
    className,
    contentIndex,
    id,
}: TabsContentProps) => {
    const {
        websiteContent,
        setWebsiteContent,
    } = useWebsiteBuilder() as WebsiteBuilder_tabsExists;

    const tabsContent = websiteContent[contentIndex].tabsContent;

    const requiresPaidPlan = true;

    const [showEditTabsModal, setShowEditTabsModal] = useState<boolean>(false);
    const [showColorPickerModal, setShowColorPickerModal] = useState<boolean>(false);
    const [selectedTabIndexColor, setSelectedTabIndexColor] = useState<number>(0);

    const handleAddTab = () => {
        if (tabsContent.tabs.length < 3) {
            const updatedContent = [...websiteContent];
            updatedContent[contentIndex].tabsContent.tabs.push({
                title: `Tab ${tabsContent.tabs.length + 1}`,
                id: `tab${tabsContent.tabs.length + 1}`
            });
            setWebsiteContent(updatedContent);
        }
    };

    const handleRemoveTab = (tabIndex: number) => {
        const updatedContent = [...websiteContent];
        updatedContent[contentIndex].tabsContent.tabs.splice(tabIndex, 1);
        setWebsiteContent(updatedContent);
    };

    const handleUpdateTabTitle = (tabIndex: number, value: string) => {
        const updatedContent = [...websiteContent];
        updatedContent[contentIndex].tabsContent.tabs[tabIndex].title = value;
        setWebsiteContent(updatedContent);
    };

    const handleSelectTabColor = (tabIndex: number, color: string) => {
        const updatedContent = [...websiteContent];
        updatedContent[contentIndex].tabsContent.tabs[tabIndex].color = color;
        setWebsiteContent(updatedContent);
    };

    const handleUpdateTabColorEnabled = (tabIndex: number, checked: boolean) => {
        const updatedContent = [...websiteContent];
        updatedContent[contentIndex].tabsContent.tabs[tabIndex].isColorEnabled = checked;
        setWebsiteContent(updatedContent);
    };

    return (
        <>
            <CardSymphony
                className={clsx("tabsContentComponent", className)}
                style={styx(sty.tabsContentComponent)}
                size="small"
                id={id}
            >
                <WebsiteBuilderHeader
                    contentIndex={contentIndex}
                    rightSideContent={
                        <>
                            <Icon
                                className="addTabIcon"
                                icon="edit"
                                color={Colors.greyDark}
                                onClick={() => setShowEditTabsModal(true)}
                            />
                        </>
                    }
                />
                <div className="tabsContainer" style={styx(sty.tabsContainer)}>
                    <TabsSymphony
                        className="tabsContentInner"
                        tabs={tabsContent.tabs}
                        selectedTab={tabsContent.activeTab}
                        onChange={(tab) => {
                            const updatedContent = [...websiteContent];
                            const tabItem = tab as TabItem;
                            updatedContent[contentIndex].tabsContent.activeTab = tabItem.id;
                            setWebsiteContent(updatedContent);
                        }}
                    />
                </div>
                <WebsiteBuilderFooter
                    className="tabsContentFooter"
                    contentIndex={contentIndex}
                    isBackgroundColor
                    isTextColor
                    requiresPaidPlan={requiresPaidPlan}
                    titleRequired="The Tabs content requires a paid plan to publish your website."
                />
            </CardSymphony>

            {/* MDOALS */}
            <ModalSymphony
                className="tabsContentModal"
                style={{
                    gap: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                isOpen={showEditTabsModal}
                onClose={() => setShowEditTabsModal(false)}
                title="Edit Tabs"
                subtitle="Edit the tabs for this content"
                buttonRightText="Done"
                buttonRightVariant="outlined"
                onClickButtonRight={() => setShowEditTabsModal(false)}
            >
                {tabsContent.tabs.map((tab, index) => (
                    <div
                        key={makeKey(tab.id, index)}
                        className="tabInputWrapper"
                        style={styx(sty.tabInputWrapper)}
                    >
                        <InputSymphony
                            className="tabTitleInput"
                            style={styx(sty.tabTitleInput)}
                            placeholder={`Tab ${index + 1} Title`}
                            value={tab.title}
                            onChange={value => handleUpdateTabTitle(index, value)}
                        />
                        <ColorPickerInput
                            className="colorPickerInput"
                            selectedColor={tab.color || '#FFFFFF'}
                            selectColor={(color) => handleSelectTabColor(index, color)}
                            style={{
                                opacity: tab.isColorEnabled ? 1 : 0.3,
                            }}
                            onClick={() => {
                                setSelectedTabIndexColor(index);
                                setShowColorPickerModal(true)
                            }}
                        />
                        <SwitchSymphony
                            className="colorEnabledSwitch"
                            checked={tab.isColorEnabled}
                            onChange={(checked) => handleUpdateTabColorEnabled(index, checked)}
                        />
                        <Icon
                            className="removeTabIcon"
                            style={styx(sty.removeTabIcon)}
                            icon="closeCircle"
                            color={Colors.greyDark}
                            onClick={() => handleRemoveTab(index)}
                        />
                    </div>
                ))}
                <ButtonSymphony
                    className="addTabButton"
                    style={{paddingLeft: 12}}
                    iconLeft="addCircle"
                    onClick={handleAddTab}
                    disabled={tabsContent.tabs.length >= 3}
                >
                    Add Tab
                </ButtonSymphony>
            </ModalSymphony>

            <ModalSymphony
                className="websiteBuilderColorPickerModal"
                isOpen={showColorPickerModal}
                onClose={() => setShowColorPickerModal(false)}
                style={{
                    gap: 8,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                size="small"
            >
                <MultiPalette
                    className="multiPalette"
                    style={sty.multiPalette}
                    selectedColor={tabsContent.tabs[selectedTabIndexColor].color || '#FFFFFF'}
                    selectColor={(color) => {
                        handleSelectTabColor(selectedTabIndexColor, color);
                    }}
                    showBlackAndWhite={false}
                    size={32}
                />
            </ModalSymphony>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    tabsContentComponent: {
    },
    tabsContainer: {
        display: 'flex',
        width: '100%',
        gap: 8
    },
    tabInputWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    tabTitleInput: {
        minWidth: 60,
        width: 'fit-content'
    },
    removeTabIcon: {
        flexShrink: 0
    }
}

export default TabsContent;